import * as React from "react"
import { User, LoginState, isLoggedInState } from "../components/portal/state";
import { Action, loadingAsyncState } from "widgets-for-react";
import { State, ExtraUserInfo } from "./portal_client";
import i18next = require("i18next");
import { ProductConfiguration } from "./product_configuration";
import { getNewQuotation } from "./portal_utils/api"
import { pushEventToDatalayer } from '../utils/analytics';
import { CharacteristicValue, RequestToRetry } from './portal/types';

type OverlayProps = {
  user: () => User<ExtraUserInfo>,
  setState: (_: Action<LoginState<State, ExtraUserInfo>>) => void,
  state: LoginState<State, ExtraUserInfo>,
} & ({ type: "panel" } | ({ type: "alert" } & AlertProps))

type AlertProps = {
  alertType: "cancelConfiguration"
           | "connectionErrorServer"
           | "connectionErrorUser"
           | "editCharacteristic"
           | "logout"
           | "quotationSubmit"
           | "deleteProduct"
}

export const alertMessage = (alertType: AlertProps["alertType"], props: OverlayProps): JSX.Element => {
  let title: string;
  let text: string | JSX.Element;
  let buttonPrimary: { text: string, handleClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void };
  let buttonSecondary: { text: string, handleClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void };
  let products = props.state.appState.products
  let deleteProductConfigurationKey = props.state.appState.deleteProductConfigurationKey
  let productConfigurations = props.state.appState.productConfigurations
  // TODO check `edit-characteristic` text (not sure if all answers or only related answers are removed)
  switch (alertType) {
    case "cancelConfiguration":
      title = i18next.t(`alerts:cancelConfiguration.title`);
      text = i18next.t(`alerts:cancelConfiguration.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:cancelConfiguration.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          document.documentElement.classList.remove('panel--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              let productConfigurations = s.appState.productConfigurations.filter(pc => pc.Submitted).toMap()
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  isPanelOpen: !s.appState.isPanelOpen,
                  selectedProductConfiguration: "0",
                  productConfigurations,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    cancelConfiguration: !s.appState.isAlertTypeOpen.cancelConfiguration
                  },
                  cachedCharacteristics: []
                }
              })
            }
          })
        }
      };
      buttonSecondary = {
        text: `${i18next.t(`alerts:cancelConfiguration.buttonSecondary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    cancelConfiguration: !s.appState.isAlertTypeOpen.cancelConfiguration
                  }
                }
              })
            }
          })
        }
      };
      break;
    case "connectionErrorServer":
      title = i18next.t(`alerts:connectionErrorServer.title`);
      text = i18next.t(`alerts:connectionErrorServer.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:connectionErrorServer.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled')
          props.setState(s => {
            if (isLoggedInState(s)) {
              let products = s.appState.toRetry.kind == "products" ? s.appState.toRetry.loading : s.appState.products
              let quotation_response = s.appState.toRetry.kind == "quotation_response" ? s.appState.toRetry.loading : s.appState.quotation_response
              let productConfigurations = s.appState.toRetry.kind == "productConfigurations" ? s.appState.toRetry.loading : s.appState.productConfigurations
              let toRetry: RequestToRetry = { kind: "none" }
              return {
                ...s,
                appState: {
                  ...s.appState,
                  products,
                  quotation_response,
                  productConfigurations,
                  toRetry,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    connectionErrorServer: false
                  }
                }
              }
            }
          })
        }
      }
      break;
    case "connectionErrorUser":
      title = i18next.t(`alerts:connectionErrorUser.title`);
      text = i18next.t(`alerts:connectionErrorUser.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:connectionErrorUser.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled')
          props.setState(s => {
            if (isLoggedInState(s)) {
              let products = s.appState.toRetry.kind == "products" ? s.appState.toRetry.loading : s.appState.products
              let quotation_response = s.appState.toRetry.kind == "quotation_response" ? s.appState.toRetry.loading : s.appState.quotation_response
              let productConfigurations = s.appState.toRetry.kind == "productConfigurations" ? s.appState.toRetry.loading : s.appState.productConfigurations
              let toRetry: RequestToRetry = { kind: "none" }
              return {
                ...s,
                appState: {
                  ...s.appState,
                  products,
                  quotation_response,
                  productConfigurations,
                  toRetry,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    connectionErrorUser: false
                  }
                }
              }
            }
          })
        }
      }
      break;
    case "deleteProduct":
      title = i18next.t(`alerts:deleteProduct.title`);
      text = (products.kind == 'loaded' && deleteProductConfigurationKey != "")
        ? (<>
            {i18next.t(`alerts:deleteProduct.product`, { product: `${(products.value.find(x => x.Id == productConfigurations.get(deleteProductConfigurationKey).ProductId).Name)}` })}
            {i18next.t(`alerts:deleteProduct.keyWithCount`, { count: productConfigurations.get(deleteProductConfigurationKey).Count })}
          </>)
        : "";
      buttonPrimary = {
        text: `${i18next.t(`alerts:deleteProduct.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled')
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  productConfigurations: s.appState.productConfigurations.remove(s.appState.deleteProductConfigurationKey),
                  deleteProductConfigurationKey: "",
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    deleteProduct: !s.appState.isAlertTypeOpen.deleteProduct
                  }
                }
              });
            }
          });
        }
      };
      buttonSecondary = {
        text: `${i18next.t(`alerts:deleteProduct.buttonSecondary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  deleteProductConfigurationKey: "",
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    deleteProduct: !s.appState.isAlertTypeOpen.deleteProduct
                  }
                }
              })
            }
          })
        }
      };
      break;
    case "editCharacteristic":
      title = i18next.t(`alerts:editCharacteristic.title`);
      text = i18next.t(`alerts:editCharacteristic.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:editCharacteristic.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled')
          props.setState(s => {
            if (isLoggedInState(s)) {
              let { productConfigurations } = s.appState
              let productConf = productConfigurations.get(s.appState.selectedProductConfiguration)
              let charateristicId = s.appState.editCharateristicId
              let CharacteristicValues: CharacteristicValue[] = []
              let temporaryCharacteristicValues: CharacteristicValue[] = []
              productConf.SelectedCharacteristic = charateristicId
              productConf.CharacteristicValues.forEach(cv => {
                if (cv.CharacteristicId == charateristicId) {
                    CharacteristicValues = temporaryCharacteristicValues
                  } else {
                    temporaryCharacteristicValues.push(cv)
                  }
              })
              productConf.CharacteristicValues = CharacteristicValues
              if (charateristicId == "ProductId") {
                productConf.Count = 0
              }
              return {
                ...s,
                appState: {
                  ...s.appState,
                  productConfigurations: productConfigurations.set(s.appState.selectedProductConfiguration, productConf),
                  editCharateristicId: "",
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    editCharacteristic: !s.appState.isAlertTypeOpen.editCharacteristic
                  }
                }
              }
            }
          })
        }
      };
      buttonSecondary = {
        text: `${i18next.t(`alerts:editCharacteristic.buttonSecondary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  editCharateristicId: "",
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    editCharacteristic: !s.appState.isAlertTypeOpen.editCharacteristic
                  }
                }
              })
            }
          })
        }
      };
      break;
    case "logout":
      title = i18next.t(`alerts:logout.title`);
      text = i18next.t(`alerts:logout.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:logout.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled')
          props.setState(s => s.status != "logged in" ? s : ({ ...s, status: "logged in - logging out" }))
        }
      };
      buttonSecondary = {
        text: `${i18next.t(`alerts:logout.buttonSecondary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    logout: !s.appState.isAlertTypeOpen.logout
                  }
                }
              })
            }
          })
        }
      };
      break;
    case "quotationSubmit":
      title = i18next.t(`alerts:quotationSubmit.title`);
      text = i18next.t(`alerts:quotationSubmit.text`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:quotationSubmit.buttonPrimary`)}`,
        handleClick: e => {
          pushEventToDatalayer({ category: "Productconfigurator", action: "Send quotation request" })
          document.documentElement.classList.remove('alert--toggled');
          let state = props.state.appState
          let quotationResponse = loadingAsyncState(() =>
              getNewQuotation({
              CustomerId: isLoggedInState(props.state) ? props.state.user.SAPId : "",
              LanguageCode: state.language_code,
                Name: state.quotation_request.project_name,
                ProductConfigurations: state.productConfigurations.toArray(),
                ProjectAddress: state.quotation_request.project_address,
                ProjectCity: state.quotation_request.project_city,
                ProjectCountry: state.quotation_request.project_country,
                ProjectName: state.quotation_request.project_name,
              RequiredDeliveryDate: state.quotation_request.required_delivery_date,
              Products: state.products.kind == "loaded" ? state.products.value : null,
              CharacteristicDefinitions: state.productConfigurations.toArray()
                .map(x => x.CharacteristicArray.kind == "loaded" ? x.CharacteristicArray.value : null).reduce((a,b)=> a.concat(b))
              }).then(result => {
                  return result
            })
          )

          props.setState(s => {
            if (isLoggedInState(s)) {
              let prodconfigs = s.appState.productConfigurations
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    quotationSubmit: !s.appState.isAlertTypeOpen.quotationSubmit
                  },
                  quotation_response: quotationResponse
                }
              })
            }
          })
        }
      };
      buttonSecondary = {
        text: `${i18next.t(`alerts:quotationSubmit.buttonSecondary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
          props.setState(s => {
            if (isLoggedInState(s)) {
              return ({
                ...s,
                appState: {
                  ...s.appState,
                  isAlertTypeOpen: {
                    ...s.appState.isAlertTypeOpen,
                    quotationSubmit: !s.appState.isAlertTypeOpen.quotationSubmit
                  }
                }
              })
            }
          })
        }
      };
      break;
    default:
      // TODO include default error handler?
      title = i18next.t(`alerts:default.title`);
      buttonPrimary = {
        text: `${i18next.t(`alerts:default.buttonPrimary`)}`,
        handleClick: e => {
          document.documentElement.classList.remove('alert--toggled');
        }
      };
  }
  return (
    <>
      <header className="alert__header">
        <span className="title--m">{title}</span>
      </header>

      <p className="alert__text text--m">{text}</p>

      <footer className="alert__footer">
        {buttonPrimary && buttonPrimary.text !== "" && (
          <button type="button" className="button button--primary" onClick={e => buttonPrimary.handleClick(e)}>{buttonPrimary.text}</button>
        )}

        {buttonSecondary && buttonSecondary.text !== "" && (
          <button type="button" className="button button--secondary" onClick={e => buttonSecondary.handleClick(e)}>{buttonSecondary.text}</button>
        )}
      </footer>
    </>
  )
}

export const Overlay: React.StatelessComponent<OverlayProps> = (props: OverlayProps) => {
  let children =
    props.type === "panel"
      ? <div className="panel__content">
          <header className="panel__header">
            <button
              type="button"
              className="panel__button link link--arrowed"
              id="a11y-overlay-collapse"
              aria-hidden={!props.state.appState.isPanelOpen}
              onClick={e => {
                document.documentElement.classList.add('alert--toggled');
                props.setState(s => {
                  if (isLoggedInState(s)) {
                    return ({ ...s, appState: { ...s.appState, isAlertTypeOpen: { ...s.appState.isAlertTypeOpen, cancelConfiguration: true } } })
                  }
                })
              }}
            >
              <span className="link__label">{i18next.t(`quotation_request:product.clickables.cancel`)}</span>
              <svg className="link__icon" width="20px" height="20px" aria-hidden="true">
                <use xlinkHref="#icon--arrow" />
              </svg>
            </button>
          </header>
          {isLoggedInState(props.state) && props.state.appState.showProducts
            ? <ProductConfiguration {...props} />
            : <></>
          }
        </div>
      : props.type === "alert"
        ? alertMessage(props.alertType, props)
        : <></>

  return (
    <section
      hidden={
        props.type === "panel"
          ? !props.state.appState.isPanelOpen
          : props.type === "alert"
            ? !props.state.appState.isAlertTypeOpen[props.alertType]
            : null
        }
      className={`overlay ${props.type} ${props.type === "alert" ? `alert--${props.alertType}` : ""}`}
    >
      <div className={`overlay__inner ${props.type}__inner`}>
        {children}
      </div>

      <div
        className="overlay__bg"
        onClick={() => {
          if ((props.type === "alert" && props.alertType !== "connectionErrorUser") || (props.type === "alert" && props.alertType !== "connectionErrorServer")) {
            document.documentElement.classList.remove('alert--toggled');
            props.setState((s) => {
              if (isLoggedInState(s)) {
                return ({
                  ...s,
                  appState: {
                    ...s.appState,
                    deleteProductConfigurationKey: "",
                    editCharateristicId: "",
                    isAlertTypeOpen: {
                      ...s.appState.isAlertTypeOpen,
                      [props.alertType]: false
                    }
                  }
                })
              }
            })
          } else if (props.type === "panel") {
            props.setState(s => {
              if (isLoggedInState(s)) {
                return ({ ...s, appState: { ...s.appState, isAlertTypeOpen: { ...s.appState.isAlertTypeOpen, cancelConfiguration: true } } })
              }
            })
          }
          return false;
        }}
      />
    </section>
  )
}